import { useId, type ReactElement } from 'react';
import type { ConversationItem } from '@chatapp-inbox/shared';
import { useConversationStatus } from '../conversation';
import { Button } from './Button';
import { StatusRegion } from './StatusRegion';
import { useStatusMessage } from './use-status-message';
import shared from './shared.module.css';
import styles from './StatusSection.module.css';

export const CLOSED_TEXT = 'Conversa encerrada.';
export const REOPENED_TEXT = 'Conversa reaberta.';

/**
 * Seção "Encerramento": quem pode agir na conversa (o responsável ou um administrador) a encerra ou
 * reabre. Encerrar tira a conversa das abas de trabalho; o responsável continua o mesmo, e ela volta
 * sozinha quando o cliente escreve ou alguém envia uma mensagem (docs/conversation-status.md). O estado
 * em si (aberta ou encerrada) já aparece no cartão do contato, então aqui só se explica e se age.
 *
 * É um único botão cujo texto muda: depois de encerrar ele vira "Reabrir conversa" SEM sair do lugar,
 * então o foco de quem usa teclado continua nele. Quem remonta esta seção por conversa (`key`)
 * garante que avisos e erros não passem de uma conversa para a outra.
 */
export function StatusSection({ conversation }: { conversation: ConversationItem }): ReactElement {
  const headingId = useId();
  const { change, pending, error } = useConversationStatus(conversation.id);
  const status = useStatusMessage();
  const closed = conversation.status === 'closed';
  const next = closed ? 'open' : 'closed';

  const run = (): void => {
    if (pending) return;
    // O aviso do resultado anterior ("Conversa encerrada.") não pode ficar ao lado desta tentativa.
    status.announce('');
    change(next, { onSuccess: () => status.announce(closed ? REOPENED_TEXT : CLOSED_TEXT) });
  };

  return (
    <section aria-labelledby={headingId} className={shared.section}>
      <h3 id={headingId} tabIndex={-1} className={shared.heading}>
        Encerramento
      </h3>

      <p className={shared.hint}>
        {closed
          ? 'Esta conversa está fora das abas de trabalho. Volta sozinha quando o cliente escrever ou quando alguém enviar uma mensagem.'
          : 'Ao encerrar, a conversa sai das abas de trabalho e continua na aba “Encerradas” e na busca.'}
      </p>

      {conversation.canWrite ? (
        <div className={styles.actions}>
          <Button busy={pending} onClick={run}>
            {pending
              ? closed
                ? 'Reabrindo…'
                : 'Encerrando…'
              : closed
                ? 'Reabrir conversa'
                : 'Encerrar conversa'}
          </Button>
        </div>
      ) : (
        <p className={shared.hint}>
          Só a pessoa responsável ou um administrador pode encerrar ou reabrir a conversa.
        </p>
      )}

      <StatusRegion message={status.message} />
      {error && (
        <p role="alert" className={shared.alert}>
          {error}
        </p>
      )}
    </section>
  );
}
